/**
 * O certificado desenhado pelo pipeline. Sem uma única decisão de layout.
 *
 * Este componente não sabe o que é um `conclusionText`, não conhece a regra da
 * transparência, não decide se um campo aparece. Ele recebe a lista de
 * instruções que o `resolve` produziu e a entrega ao `renderSvg` — a mesma
 * lista que o `renderPdf` recebe no servidor. Por isso os dois não têm o que
 * discordar.
 *
 * O QUE ELE SUBSTITUI
 *
 * O `CertificatePreview` anterior tinha 687 linhas e decidia por conta própria:
 * 38 chamadas a `getStyle(id)` com fallback, a regra "cor própria anula
 * opacity" repetida 8 vezes, posições calculadas ali mesmo. O worker de e-mail
 * tomava as MESMAS decisões, em outro arquivo, e chegou a 15 divergências
 * ativas. Não divergiram por azar: uma regra escrita em dois lugares diverge,
 * é aritmética.
 *
 * RECEBE O `ResolvedCertificate`, NÃO O SNAPSHOT
 *
 * De propósito. Quem monta a tela de edição precisa das MESMAS posições para
 * desenhar as áreas de arrasto; se este componente resolvesse por dentro, a
 * interação e o desenho poderiam divergir. Um `resolve` só, no pai, e os dois
 * olham para o mesmo resultado.
 *
 * SOBRE `dangerouslySetInnerHTML`
 *
 * A saída do `renderSvg` vem de um pintor que escapa `&`, `<`, `>`, `"` e `'`
 * em todo texto — `render.test.ts` verifica com um caso que tenta injetar
 * `<script>`. Não há caminho de dado do produtor que chegue aqui sem passar
 * por lá.
 */

import { useMemo } from "react";

import {
  renderSvg,
  type AssetId,
  type ResolvedCertificate,
} from "@tickethall/core/certificate";

/** Medidas do preview antigo, mantidas para os contêineres não mudarem. */
export const CERTIFICATE_WIDTH = 1123;
export const CERTIFICATE_HEIGHT = 794;

export interface CertificateVectorProps {
  resolved: ResolvedCertificate;
  /** URL pública de cada asset. Sem isto, o fundo não é desenhado. */
  assetUrl?: (id: AssetId) => string;
  className?: string;
}

export function CertificateVector({ resolved, assetUrl, className }: CertificateVectorProps) {
  const svg = useMemo(
    () =>
      // O viewBox sai em pontos; `width`/`height` em px encaixam o desenho na
      // caixa que o editor já usa, sem mexer em nenhuma medida de tela.
      renderSvg(resolved, (id) => assetUrl?.(id) ?? "").replace(
        /^<svg /,
        `<svg width="${CERTIFICATE_WIDTH}" height="${CERTIFICATE_HEIGHT}" preserveAspectRatio="none" `,
      ),
    [resolved, assetUrl],
  );

  return <div className={className} dangerouslySetInnerHTML={{ __html: svg }} />;
}
